import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/api/AuthContext';
import {
  Mail,
  Lock,
  Loader2,
  AlertCircle,
  Eye,
  EyeOff,
  Truck,
  ChefHat,
  Package,
  ArrowRight,
} from 'lucide-react';

const TARGETS = {
  admin: '/admin/dashboard',
  agent: '/agent/tasks',
  warehouse: '/warehouse',
  shipping: '/shipping',
  buchhaltung: '/buchhaltung',
  kunde: '/kunde',
};

function getTargetForRole(role) {
  return TARGETS[(role || '').toLowerCase()] || '/agent/tasks';
}

export default function Home() {
  const navigate = useNavigate();
  const { login, user, isAuthenticated } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Zaten girişliyse otomatik olarak rol alanına yönlendir.
  useEffect(() => {
    if (isAuthenticated && user?.role) {
      navigate(getTargetForRole(user.role), { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Bitte E-Mail-Adresse eingeben.');
      return;
    }
    if (!password.trim()) {
      setError('Bitte Passwort eingeben.');
      return;
    }

    setLoading(true);
    const result = await login(email, password); // unified: rol yok
    setLoading(false);

    if (result.success) {
      navigate(getTargetForRole(result.user?.role), { replace: true });
    } else {
      setError(result.error || 'Anmeldung fehlgeschlagen.');
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col">
      {/* Full-screen Background */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url('/inmotion-bg.png')" }}
      />
      <div className="absolute inset-0 bg-brand-950/[0.92] backdrop-blur-md" />

      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 py-10">
        <div className="w-full max-w-sm space-y-7">
          {/* Logo */}
          <div className="text-center space-y-3">
            <div className="flex items-center justify-center gap-3 mb-1">
              <span
                className="text-6xl font-black text-white leading-none"
                style={{ fontFamily: "'Inter', Arial, sans-serif", letterSpacing: '-0.04em' }}
              >
                I
              </span>
              <span
                className="text-6xl font-black text-white leading-none"
                style={{ fontFamily: "'Inter', Arial, sans-serif", letterSpacing: '-0.04em' }}
              >
                M
              </span>
              <div className="grid grid-cols-4 gap-[3px] ml-1">
                {Array.from({ length: 16 }).map((_, i) => (
                  <div key={i} className="w-[3px] h-[3px] rounded-full bg-white/60" />
                ))}
              </div>
            </div>

            <h1
              className="text-base font-bold text-white tracking-[0.35em] uppercase"
              style={{ fontFamily: "'Inter', Arial, sans-serif" }}
            >
              INMOTION
            </h1>

            <p className="text-xs font-semibold text-white/80 uppercase tracking-[0.2em]">
              Immer in Bewegung
            </p>

            <div className="flex items-center justify-center gap-4 pt-1">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-amber-400/50" />
              <div className="h-1.5 w-1.5 bg-amber-400 rounded-full" />
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-amber-400/50" />
            </div>
          </div>

          {/* Unified Login Form */}
          <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
            {error && (
              <div className="flex items-start gap-3 p-3 rounded-xl bg-red-500/10 border border-red-500/20">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-300">{error}</p>
              </div>
            )}

            {/* Email */}
            <div className="space-y-1.5">
              <label htmlFor="login-email" className="text-[11px] font-medium text-white/60 uppercase tracking-wider">
                E-Mail
              </label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 pointer-events-none" />
                <input
                  id="login-email"
                  type="email"
                  inputMode="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@inmotion.de"
                  className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-white/[0.07] border border-white/10 text-white placeholder-white/25 focus:outline-none focus:bg-white/[0.1] focus:border-amber-400/50 transition-all text-base"
                  autoComplete="email"
                  autoFocus
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <label htmlFor="login-password" className="text-[11px] font-medium text-white/60 uppercase tracking-wider">
                Passwort
              </label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 pointer-events-none" />
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-11 pr-12 py-3.5 rounded-xl bg-white/[0.07] border border-white/10 text-white placeholder-white/25 focus:outline-none focus:bg-white/[0.1] focus:border-amber-400/50 transition-all text-base"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors p-1"
                  aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 rounded-xl font-semibold text-base transition-all duration-300 flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-brand-950 shadow-lg shadow-amber-500/20 disabled:bg-amber-500/50"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Anmeldung...
                </>
              ) : (
                <>
                  Anmelden
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>

            <p className="text-[11px] text-white/40 text-center leading-relaxed">
              Ihr Bereich wird nach der Anmeldung automatisch geöffnet.
            </p>
          </form>

          {/* Footer */}
          <div className="text-center pt-2 space-y-3">
            <div className="flex items-center justify-center gap-2 text-white/20">
              <div className="h-px flex-1 bg-white/10" />
              <Package className="w-3.5 h-3.5" />
              <div className="h-px flex-1 bg-white/10" />
            </div>
            <div className="flex items-center justify-center gap-3 text-amber-400/70">
              <Truck className="w-4 h-4" strokeWidth={1.5} />
              <span className="text-[10px] font-bold tracking-wider">GROSSHANDEL</span>
              <div className="h-3 w-px bg-amber-400/30" />
              <span className="text-[10px] font-bold tracking-wider">HORECA</span>
              <ChefHat className="w-4 h-4" strokeWidth={1.5} />
            </div>
            <p className="text-[9px] text-white/30 uppercase tracking-[0.25em] font-medium">
              Verpackung · Hygiene · Reinigungsmittel
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Glow */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[300px] h-[100px] bg-amber-400/5 rounded-full blur-3xl pointer-events-none" />
    </div>
  );
}
